import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MonitorSmartphone,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Trash2,
  Undo2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  ApplyOrderDiscountRequest,
  CreatePaymentRequest,
  DiscountType,
  Order,
  OrderStatus,
} from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { formatCents, formatDateTime } from "../../lib/format";
import { humaniseError } from "../../lib/humaniseError";
import { orderStatusLabelKey } from "../../lib/orderStatus";
import { KIOSK_ORDER_HANDOFF_FROM_POS } from "../../lib/permissions";
import {
  countNonTerminalCharges,
  isNonTerminalPaymentStatus,
  oldestNonTerminalChargeAgeMs,
  PENDING_PAYMENT_TIMEOUT_MS,
  sumNonTerminalChargeCents,
} from "../../lib/paymentStatus";
import { useActiveTimezone } from "../../lib/useActiveTimezone";
import { cn } from "../../lib/utils";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { InlineSpinner, PageSpinner } from "../ui/Spinner";
import { OrderStatusBadge } from "../ui/StatusBadge";
import { OrderReceiptsModal } from "../receipt/OrderReceiptsModal";
import { CancelPaymentDialog } from "./CancelPaymentDialog";
import { EditOrderDialog } from "./EditOrderDialog";
import { KioskHandoffModal } from "./KioskHandoffModal";
import { MethodPicker, type PaymentOutcome } from "./MethodPicker";
import { RefundPanel } from "./RefundPanel";
import { Timeline } from "./Timeline";

const COLLECTABLE_STATUSES: ReadonlySet<OrderStatus> = new Set([
  "open",
  "partially_paid",
  "partially_refunded",
  // A fully-refunded order has remaining_cents = total - paid + refunded > 0.
  // The cashier must be able to take a replacement payment without creating a
  // new order. Only "paid" and "voided" are true terminal states.
  "refunded",
]);

export interface OrderDetailViewProps {
  readonly orderId: string;
  readonly listOrder?: Order;
  readonly mode?: "inline" | "page";
  /** When provided, replaces the "← Back to transactions" link with a custom action. */
  readonly onBack?: () => void;
  readonly backLabel?: string;
}

export function OrderDetailView({
  orderId,
  listOrder,
  mode = "page",
  onBack,
  backLabel,
}: OrderDetailViewProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tz = useActiveTimezone();
  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement | null>(null);
  const refundRef = useRef<HTMLDivElement | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [lastPaymentOutcome, setLastPaymentOutcome] = useState<PaymentOutcome | null>(null);
  const [cancellingPaymentId, setCancellingPaymentId] = useState<string | null>(null);
  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [metaExpanded, setMetaExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [kioskHandoffOpen, setKioskHandoffOpen] = useState(false);
  const [isPaymentFormActive, setIsPaymentFormActive] = useState(false);
  const [isRefundFormActive, setIsRefundFormActive] = useState(false);
  const [voidingDiscountId, setVoidingDiscountId] = useState<string | null>(null);
  const [voidDiscountSubmitting, setVoidDiscountSubmitting] = useState(false);
  const [voidDiscountError, setVoidDiscountError] = useState<string | null>(null);
  const [addDiscountOpen, setAddDiscountOpen] = useState(false);
  const [addDiscountTypeId, setAddDiscountTypeId] = useState("");
  const [addDiscountMode, setAddDiscountMode] = useState<"amount" | "percent">("amount");
  const [addDiscountInput, setAddDiscountInput] = useState("");
  const [addDiscountReason, setAddDiscountReason] = useState("");
  const [addDiscountSubmitting, setAddDiscountSubmitting] = useState(false);
  const [addDiscountError, setAddDiscountError] = useState<string | null>(null);
  const isAnyFormActive = isPaymentFormActive || isRefundFormActive;

  useEffect(() => {
    if (paymentError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [paymentError]);

  useEffect(() => {
    if (!refundOpen) return;
    const id = setTimeout(() => {
      refundRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    return () => clearTimeout(id);
  }, [refundOpen]);

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  const envelope = useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => getPosClient().getOrder(orderId),
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const anyNonTerminal = data.payments.some((p) => isNonTerminalPaymentStatus(p.status));
      return anyNonTerminal ? (mode === "inline" ? 5_000 : 8_000) : false;
    },
  });

  const discountTypesQuery = useQuery({
    queryKey: ["pos", "discount-types"],
    queryFn: () => getPosClient().listDiscountTypes(),
    staleTime: 60_000,
  });
  const discountTypes: readonly DiscountType[] = discountTypesQuery.data?.discount_types ?? [];

  const env = envelope.data;
  const order = env?.order ?? listOrder;
  const deptName = env?.order.department_name ?? listOrder?.department_name;

  const inFlightCents = env ? sumNonTerminalChargeCents(env.payments) : 0;
  const inFlightCount = env ? countNonTerminalCharges(env.payments) : 0;
  const hasInFlightCharge = inFlightCents > 0;
  const netRemainingCents = env
    ? Math.max(0, env.order.remaining_cents - inFlightCents)
    : Math.max(0, (order?.total_cents ?? 0) - (order?.paid_cents ?? 0));
  const oldestPendingAgeMs = env ? oldestNonTerminalChargeAgeMs(env.payments) : null;
  const inFlightTimedOut =
    oldestPendingAgeMs !== null && oldestPendingAgeMs > PENDING_PAYMENT_TIMEOUT_MS;
  const collectable = env
    ? COLLECTABLE_STATUSES.has(env.order.status) && netRemainingCents > 0
    : false;

  // When the order fully pays off, collectable flips to false and the
  // MethodPicker unmounts without calling onFormActiveChange(false).
  // Reset so the opacity mask clears on the paid/done state.
  useEffect(() => {
    if (!collectable) setIsPaymentFormActive(false);
  }, [collectable]);

  const hasApprovedPayment = env
    ? env.payments.some((p) => p.role === "charge" && p.status === "APPROVED")
    : false;
  const collectTitleKey = hasApprovedPayment
    ? "orderDetail.continueCollectTitle"
    : "orderDetail.takePaymentTitle";

  const canRefundPermission = (profile.data?.permissions ?? []).some(
    (p) => p === "pos.payment.refund" || p === "pos.payment.refund_own",
  );
  const showRefundButton =
    env != null &&
    canRefundPermission &&
    env.payments.some((p) => p.role === "charge" && p.status === "APPROVED") &&
    env.order.status !== "voided" &&
    env.order.status !== "refunded";

  const canKioskHandoff = (profile.data?.permissions ?? []).includes(KIOSK_ORDER_HANDOFF_FROM_POS);
  const showKioskHandoffButton = canKioskHandoff && order?.status === "open";

  // Remaining color matches OrderSummary tones
  const paidCents = order?.paid_cents ?? 0;
  const totalCents = order?.total_cents ?? 0;
  const refundedCents = order?.refunded_cents ?? 0;
  const remainingCents =
    order?.remaining_cents ?? Math.max(0, totalCents - paidCents + refundedCents);
  const remainingColorClass =
    remainingCents === 0
      ? "text-emerald-600 dark:text-emerald-400"
      : remainingCents < totalCents
        ? "text-amber-600 dark:text-amber-400"
        : "text-foreground";

  function resetAddDiscount() {
    setAddDiscountTypeId("");
    setAddDiscountMode("amount");
    setAddDiscountInput("");
    setAddDiscountReason("");
    setAddDiscountError(null);
  }

  function handleAddDiscountSubmit() {
    const dt = discountTypes.find((x) => x.id === addDiscountTypeId);
    if (!dt) {
      setAddDiscountError(t("sale.discountType") + " " + t("sale.errors.required"));
      return;
    }
    if (dt.is_other && !addDiscountReason.trim()) {
      setAddDiscountError(t("sale.discountReason") + " " + t("sale.errors.required"));
      return;
    }
    let body: ApplyOrderDiscountRequest;
    if (addDiscountMode === "percent") {
      const pct = parseFloat(addDiscountInput);
      if (isNaN(pct) || pct <= 0 || pct > 100) {
        setAddDiscountError(t("sale.errors.invalidAmount"));
        return;
      }
      body = {
        discount_type_id: addDiscountTypeId,
        percent_bps: Math.round(pct * 100),
        ...(dt.is_other ? { reason: addDiscountReason.trim() } : {}),
      };
    } else {
      const dollars = parseFloat(addDiscountInput);
      if (isNaN(dollars) || dollars <= 0) {
        setAddDiscountError(t("sale.errors.invalidAmount"));
        return;
      }
      body = {
        discount_type_id: addDiscountTypeId,
        amount_cents: Math.round(dollars * 100),
        ...(dt.is_other ? { reason: addDiscountReason.trim() } : {}),
      };
    }
    setAddDiscountSubmitting(true);
    setAddDiscountError(null);
    void getPosClient()
      .applyOrderDiscount(orderId, body)
      .then(() => {
        void envelope.refetch();
        void qc.invalidateQueries({ queryKey: ["orders"] });
        setAddDiscountOpen(false);
        resetAddDiscount();
      })
      .catch((err: unknown) => {
        setAddDiscountError(humaniseError(err));
      })
      .finally(() => setAddDiscountSubmitting(false));
  }

  function handleVoidDiscount(discountId: string) {
    setVoidDiscountError(null);
    setVoidDiscountSubmitting(true);
    void getPosClient()
      .voidOrderDiscount(orderId, discountId)
      .then(() => {
        void envelope.refetch();
        void qc.invalidateQueries({ queryKey: ["orders"] });
        setVoidingDiscountId(null);
        setVoidDiscountError(null);
      })
      .catch((err: unknown) => {
        setVoidDiscountError(humaniseError(err));
      })
      .finally(() => setVoidDiscountSubmitting(false));
  }

  function handlePaymentSubmit(body: CreatePaymentRequest) {
    setPaymentError(null);
    setLastPaymentOutcome(null);
    setSubmitting(true);
    void getPosClient()
      .createPayment(orderId, body)
      .then((env) => {
        void qc.invalidateQueries({ queryKey: ["orders"] });
        void envelope.refetch();
        // Derive pending vs success from the actual returned payment status
        // rather than guessing from the request type. Card-present can come
        // back PENDING (e.g. async terminal flow) and must not show the
        // "payment received" success banner.
        const isNowPending = env.payments.some(
          (p) => p.role === "charge" && isNonTerminalPaymentStatus(p.status),
        );
        setLastPaymentOutcome({
          kind: isNowPending ? "pending" : "success",
          amountCents: body.amount_cents,
        });
      })
      .catch((err: unknown) => {
        setLastPaymentOutcome({
          kind: "error",
          amountCents: body.amount_cents,
          message: humaniseError(err),
        });
      })
      .finally(() => setSubmitting(false));
  }

  // ── INLINE MODE ────────────────────────────────────────────────────────────
  if (mode === "inline") {
    return (
      <div className="border-l-2 border-brand-primary/30 bg-brand-primary/[0.025] px-4 py-3 sm:px-6">
        <div className="max-w-5xl">
          {/* Action bar */}
          <div
            className={cn(
              "mb-3 flex flex-wrap items-center justify-end gap-1.5 transition-opacity",
              isAnyFormActive && "pointer-events-none opacity-40",
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              leftIcon={
                envelope.isFetching ? (
                  <InlineSpinner />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                )
              }
              disabled={envelope.isFetching}
              onClick={() => envelope.refetch()}
            >
              {t("transactions.refreshOrder")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ReceiptText className="h-3.5 w-3.5" />}
              disabled={!env}
              onClick={() => setReceiptsOpen(true)}
            >
              {t("receipt.title")}
            </Button>
            {showRefundButton && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Undo2 className="h-3.5 w-3.5" />}
                onClick={() => setRefundOpen(true)}
              >
                {t("sale.refund")}
              </Button>
            )}
            <a
              href={`/transactions/${orderId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-tap items-center gap-1.5 rounded-md px-3 text-sm font-medium text-foreground hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {t("transactions.openDetailPage")}
            </a>
          </div>

          {/* Meta card - header always shows label + summary + financials; body collapsible */}
          <div
            className={cn(
              "mb-4 rounded-xl border border-line bg-surface transition-opacity",
              isAnyFormActive && "pointer-events-none opacity-40",
            )}
          >
            <button
              type="button"
              onClick={() => setMetaExpanded((x) => !x)}
              className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
            >
              <div className="min-w-0 flex-1">
                {/* Label + summary on the same line */}
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                    {t("orderDetail.orderDetails")}
                  </span>
                  {order && (
                    <span className="truncate text-sm font-medium text-foreground">
                      {[
                        order.external_reference ?? `#${order.id.split("-")[0]?.toUpperCase()}`,
                        order.customer_name,
                        deptName,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
                </div>
                {/* Financials always visible */}
                {order && (
                  <div className="mt-2 flex flex-wrap gap-x-12 gap-y-1">
                    <span className="flex flex-col gap-0.5">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                        {order.discount_cents > 0 ? t("sale.subtotal") : t("sale.totalDue")}
                      </span>
                      <span className="font-mono text-base font-bold tabular-nums text-strong">
                        {formatCents(totalCents, order.currency)}
                      </span>
                    </span>
                    {order.discount_cents > 0 && (
                      <span className="flex flex-col gap-0.5">
                        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                          {order.discount_label ?? t("sale.discount")}
                        </span>
                        {(order.discounts ?? []).length > 0 ? (
                          (order.discounts ?? []).map((d) => (
                            <span
                              key={d.id}
                              className="font-mono text-base tabular-nums text-emerald-600 dark:text-emerald-400"
                            >
                              {d.percent_bps != null
                                ? t("receipt.discountWithPercent", {
                                    label: d.discount_type_display_name,
                                    percent: (d.percent_bps / 100).toFixed(2).replace(/\.?0+$/, ""),
                                  })
                                : d.discount_type_display_name}{" "}
                              -{formatCents(d.amount_cents, order.currency)}
                            </span>
                          ))
                        ) : (
                          <span className="font-mono text-base tabular-nums text-emerald-600 dark:text-emerald-400">
                            -{formatCents(order.discount_cents, order.currency)}
                          </span>
                        )}
                      </span>
                    )}
                    {order.discount_cents > 0 && (
                      <span className="flex flex-col gap-0.5">
                        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                          {t("sale.totalDue")}
                        </span>
                        <span className="font-mono text-base font-bold tabular-nums text-strong">
                          {formatCents(totalCents - order.discount_cents, order.currency)}
                        </span>
                      </span>
                    )}
                    <span className="flex flex-col gap-0.5">
                      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                        {t("sale.paid")}
                      </span>
                      <span
                        className={cn(
                          "font-mono text-base tabular-nums",
                          paidCents > 0
                            ? "font-semibold text-emerald-600 dark:text-emerald-400"
                            : "text-muted",
                        )}
                      >
                        {formatCents(paidCents, order.currency)}
                      </span>
                    </span>
                    {refundedCents > 0 && (
                      <span className="flex flex-col gap-0.5">
                        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                          {t("sale.refunded")}
                        </span>
                        <span className="font-mono text-base tabular-nums text-amber-600 dark:text-amber-400">
                          {formatCents(refundedCents, order.currency)}
                        </span>
                      </span>
                    )}
                    {COLLECTABLE_STATUSES.has(order.status) && remainingCents > 0 && (
                      <span className="flex flex-col gap-0.5">
                        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                          {t("sale.remaining")}
                        </span>
                        <span
                          className={cn(
                            "font-mono text-base font-bold tabular-nums",
                            remainingColorClass,
                          )}
                        >
                          {formatCents(remainingCents, order.currency)}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
              {metaExpanded ? (
                <ChevronUp className="mt-1 h-4 w-4 shrink-0 text-muted" />
              ) : (
                <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted" />
              )}
            </button>

            {/* Expandable detail grid */}
            {metaExpanded && order && (
              <div className="border-t border-line px-4 pb-4 pt-3">
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                  <DetailCell label={t("transactions.colReference")}>
                    <span className="font-mono text-sm">
                      {order.external_reference ??
                        `#${order.id.split("-")[0]?.toUpperCase() ?? ""}`}
                    </span>
                  </DetailCell>
                  <DetailCell label={t("transactions.colDate")}>
                    <span className="tabular-nums">{formatDateTime(order.created_at, tz)}</span>
                  </DetailCell>
                  <DetailCell label={t("transactions.colCustomer")}>
                    <div className="flex flex-col">
                      <span>{order.customer_name ?? <span className="text-faint">-</span>}</span>
                      {order.customer_phone && (
                        <span className="font-mono text-xs text-muted">{order.customer_phone}</span>
                      )}
                      {order.customer_email && (
                        <span className="text-xs text-muted">{order.customer_email}</span>
                      )}
                    </div>
                  </DetailCell>
                  <DetailCell label={t("transactions.colDepartment")}>
                    {deptName ?? <span className="text-faint">-</span>}
                  </DetailCell>
                </div>
                {order.description && (
                  <div className="mt-3">
                    <p
                      className={cn(
                        "text-sm text-muted",
                        !descriptionExpanded && "line-clamp-1",
                        descriptionExpanded && "whitespace-pre-line",
                      )}
                    >
                      {order.description}
                    </p>
                    {(order.description.includes("\n") || order.description.length > 60) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDescriptionExpanded((x) => !x);
                        }}
                        className="mt-0.5 text-xs font-medium text-brand-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                      >
                        {descriptionExpanded ? t("common.showLess") : t("common.showMore")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live sections - all inlined (never inner function components) so
            React never unmounts MethodPicker on parent state changes. */}
          {envelope.isPending && !env ? (
            <p className="text-sm text-muted">{t("common.loading")}</p>
          ) : envelope.isError && !env ? (
            <Alert tone="error">{t("transactions.loadError")}</Alert>
          ) : env ? (
            <div className="flex flex-col gap-4">
              {paymentError && (
                <div
                  ref={errorRef}
                  className={cn(
                    "transition-opacity",
                    isAnyFormActive && "pointer-events-none opacity-40",
                  )}
                >
                  <Alert tone="error">{paymentError}</Alert>
                </div>
              )}
              {hasInFlightCharge && (
                <div
                  className={cn(
                    "transition-opacity",
                    isAnyFormActive && "pointer-events-none opacity-40",
                  )}
                >
                  <Alert
                    tone={inFlightTimedOut ? "warn" : "info"}
                    title={t(
                      inFlightTimedOut
                        ? "sale.pendingPaymentTimedOutTitle"
                        : "sale.pendingPaymentNoticeTitle",
                      { count: inFlightCount },
                    )}
                  >
                    {inFlightTimedOut
                      ? t("sale.pendingPaymentTimedOutBody")
                      : netRemainingCents > 0
                        ? t("sale.pendingPaymentNoticeBody", { count: inFlightCount })
                        : t("sale.pendingPaymentNoticeBodyFull", { count: inFlightCount })}
                  </Alert>
                </div>
              )}
              {/* Refund panel first - primary action when open */}
              <div ref={refundRef}>
                <RefundPanel
                  envelope={env}
                  profile={profile.data}
                  locationId={env.order.location_id}
                  locked={isPaymentFormActive}
                  open={refundOpen}
                  onOpenChange={setRefundOpen}
                  onFormActiveChange={setIsRefundFormActive}
                />
              </div>
              {/* Take next payment */}
              {collectable && (
                <div
                  className={cn(
                    "rounded-xl border border-line bg-surface transition-opacity",
                    isRefundFormActive && "pointer-events-none opacity-40",
                  )}
                >
                  <div className="flex items-center justify-between rounded-t-xl border-b border-brand-primary/20 bg-brand-primary/5 px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary">
                      {t(collectTitleKey)}
                    </span>
                    <span
                      className={cn(
                        "font-mono text-sm font-semibold tabular-nums",
                        remainingColorClass,
                      )}
                    >
                      {formatCents(netRemainingCents, env.order.currency)}
                    </span>
                  </div>
                  <div className="p-4">
                    <MethodPicker
                      remainingCents={netRemainingCents}
                      currency={env.order.currency}
                      submitting={submitting}
                      locationId={env.order.location_id}
                      showHeading={false}
                      lastPaymentOutcome={lastPaymentOutcome}
                      onFormActiveChange={(active) => {
                        setIsPaymentFormActive(active);
                        if (!active) setLastPaymentOutcome(null);
                      }}
                      initialCustomerPhone={
                        env.order.customer?.phone ?? env.order.customer_phone ?? null
                      }
                      initialCustomerName={env.order.customer_name ?? null}
                      onSubmit={handlePaymentSubmit}
                    />
                  </div>
                </div>
              )}
              {/* Timeline */}
              <div
                className={cn(
                  "transition-opacity",
                  isAnyFormActive && "pointer-events-none opacity-40",
                )}
              >
                <Timeline
                  payments={env.payments}
                  currency={env.order.currency}
                  locationId={env.order.location_id}
                  orderId={env.order.id}
                  onCancelPayment={(paymentId) => setCancellingPaymentId(paymentId)}
                  onPrintReceipt={() => setReceiptsOpen(true)}
                  customerPhone={env.order.customer?.phone ?? env.order.customer_phone ?? null}
                />
              </div>
              {/* Discount management — only while order is open and no approved payment exists */}
              {env.order.status === "open" &&
                !env.payments.some((p) => p.role === "charge" && p.status === "APPROVED") &&
                discountTypes.length > 0 && (
                  <DiscountManagementCard
                    order={env.order}
                    discountTypes={discountTypes}
                    addDiscountOpen={addDiscountOpen}
                    addDiscountTypeId={addDiscountTypeId}
                    addDiscountMode={addDiscountMode}
                    addDiscountInput={addDiscountInput}
                    addDiscountReason={addDiscountReason}
                    addDiscountSubmitting={addDiscountSubmitting}
                    addDiscountError={addDiscountError}
                    voidingDiscountId={voidingDiscountId}
                    voidDiscountSubmitting={voidDiscountSubmitting}
                    voidDiscountError={voidDiscountError}
                    onOpenAdd={() => {
                      resetAddDiscount();
                      setAddDiscountOpen(true);
                    }}
                    onCloseAdd={() => {
                      setAddDiscountOpen(false);
                      resetAddDiscount();
                    }}
                    onAddTypeChange={setAddDiscountTypeId}
                    onAddModeChange={setAddDiscountMode}
                    onAddInputChange={setAddDiscountInput}
                    onAddReasonChange={setAddDiscountReason}
                    onAddSubmit={handleAddDiscountSubmit}
                    onVoidRequest={(id) => {
                      setVoidDiscountError(null);
                      setVoidingDiscountId(id);
                    }}
                    onVoidConfirm={handleVoidDiscount}
                    onVoidCancel={() => {
                      setVoidingDiscountId(null);
                      setVoidDiscountError(null);
                    }}
                  />
                )}
            </div>
          ) : null}
        </div>
        {/* /max-w-3xl */}

        {/* Modals */}
        {receiptsOpen && env && (
          <OrderReceiptsModal
            envelope={env}
            brand={profile.data?.brand}
            onClose={() => setReceiptsOpen(false)}
          />
        )}
        {editing && order && (
          <EditOrderDialog
            order={order}
            onClose={() => setEditing(false)}
            onSaved={() => setEditing(false)}
          />
        )}
        {cancellingPaymentId && (
          <CancelPaymentDialog
            orderId={orderId}
            paymentId={cancellingPaymentId}
            onClose={() => setCancellingPaymentId(null)}
            onCancelled={() => {
              void envelope.refetch();
              setCancellingPaymentId(null);
            }}
          />
        )}
      </div>
    );
  }

  // ── PAGE MODE ──────────────────────────────────────────────────────────────

  if (envelope.isPending && !env && !order) {
    return <PageSpinner label={t("common.loading")} />;
  }
  if ((envelope.isError || !env) && !order) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="error" title={t("orderDetail.notFoundTitle")}>
          {t("orderDetail.notFoundBody")}
        </Alert>
        <div className="mt-4 text-center">
          <Button variant="ghost" onClick={() => navigate("/transactions")}>
            {t("orderDetail.backToList")}
          </Button>
        </div>
      </div>
    );
  }

  const pageOrder = env?.order ?? order!;

  return (
    <div className="max-w-5xl space-y-4 pb-12">
      {/* Page header */}
      <header>
        <button
          type="button"
          onClick={onBack ?? (() => navigate("/transactions"))}
          className="mb-3 inline-flex min-h-tap items-center gap-1 text-xs font-medium text-muted hover:text-strong"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {backLabel ?? t("orderDetail.backToList")}
        </button>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
          <h1 className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-2xl font-bold tracking-tight text-strong">
            <span>
              {pageOrder.external_reference ?? `#${pageOrder.id.split("-")[0]?.toUpperCase()}`}
            </span>
            {pageOrder.customer_name && (
              <span className="text-base font-medium text-muted">{pageOrder.customer_name}</span>
            )}
          </h1>
          <OrderStatusBadge
            status={pageOrder.status}
            label={t(orderStatusLabelKey(pageOrder.status))}
            className="self-center"
          />
        </div>
      </header>

      {/* Action bar */}
      <div
        className={cn(
          "flex flex-wrap items-center justify-end gap-1.5 transition-opacity",
          isAnyFormActive && "pointer-events-none opacity-40",
        )}
      >
        <Button
          variant="ghost"
          size="sm"
          leftIcon={
            envelope.isFetching ? (
              <InlineSpinner />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            )
          }
          disabled={envelope.isFetching}
          onClick={() => envelope.refetch()}
        >
          {t("transactions.refreshOrder")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ReceiptText className="h-3.5 w-3.5" />}
          disabled={!env}
          onClick={() => setReceiptsOpen(true)}
        >
          {t("receipt.title")}
        </Button>
        {showRefundButton && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Undo2 className="h-3.5 w-3.5" />}
            onClick={() => setRefundOpen(true)}
          >
            {t("sale.refund")}
          </Button>
        )}
        {showKioskHandoffButton && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<MonitorSmartphone className="h-3.5 w-3.5" />}
            onClick={() => setKioskHandoffOpen(true)}
          >
            {t("kioskHandoff.buttonLabel")}
          </Button>
        )}
        {pageOrder.status === "open" && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Pencil className="h-3.5 w-3.5" />}
            onClick={() => setEditing(true)}
          >
            {t("orderDetail.edit")}
          </Button>
        )}
      </div>

      {/* Meta card - header always shows label + summary + financials; body collapsible */}
      <div
        className={cn(
          "rounded-xl border border-line bg-surface transition-opacity",
          isAnyFormActive && "pointer-events-none opacity-40",
        )}
      >
        <button
          type="button"
          onClick={() => setMetaExpanded((x) => !x)}
          className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
        >
          <div className="min-w-0 flex-1">
            {/* Label + summary on the same line */}
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                {t("orderDetail.orderDetails")}
              </span>
              <span className="truncate text-sm font-medium text-foreground">
                {[
                  pageOrder.external_reference ?? `#${pageOrder.id.split("-")[0]?.toUpperCase()}`,
                  pageOrder.customer_name,
                  deptName,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            {/* Financials always visible */}
            <div className="mt-2 flex flex-wrap gap-x-12 gap-y-1">
              <span className="flex flex-col gap-0.5">
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                  {pageOrder.discount_cents > 0 ? t("sale.subtotal") : t("sale.totalDue")}
                </span>
                <span className="font-mono text-base font-bold tabular-nums text-strong">
                  {formatCents(totalCents, pageOrder.currency)}
                </span>
              </span>
              {pageOrder.discount_cents > 0 && (
                <span className="flex flex-col gap-0.5">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                    {pageOrder.discount_label ?? t("sale.discount")}
                  </span>
                  {(pageOrder.discounts ?? []).length > 0 ? (
                    (pageOrder.discounts ?? []).map((d) => (
                      <span
                        key={d.id}
                        className="font-mono text-base tabular-nums text-emerald-600 dark:text-emerald-400"
                      >
                        {d.percent_bps != null
                          ? t("receipt.discountWithPercent", {
                              label: d.discount_type_display_name,
                              percent: (d.percent_bps / 100).toFixed(2).replace(/\.?0+$/, ""),
                            })
                          : d.discount_type_display_name}{" "}
                        -{formatCents(d.amount_cents, pageOrder.currency)}
                      </span>
                    ))
                  ) : (
                    <span className="font-mono text-base tabular-nums text-emerald-600 dark:text-emerald-400">
                      -{formatCents(pageOrder.discount_cents, pageOrder.currency)}
                    </span>
                  )}
                </span>
              )}
              {pageOrder.discount_cents > 0 && (
                <span className="flex flex-col gap-0.5">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                    {t("sale.totalDue")}
                  </span>
                  <span className="font-mono text-base font-bold tabular-nums text-strong">
                    {formatCents(totalCents - pageOrder.discount_cents, pageOrder.currency)}
                  </span>
                </span>
              )}
              <span className="flex flex-col gap-0.5">
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                  {t("sale.paid")}
                </span>
                <span
                  className={cn(
                    "font-mono text-base tabular-nums",
                    paidCents > 0
                      ? "font-semibold text-emerald-600 dark:text-emerald-400"
                      : "text-muted",
                  )}
                >
                  {formatCents(paidCents, pageOrder.currency)}
                </span>
              </span>
              {refundedCents > 0 && (
                <span className="flex flex-col gap-0.5">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                    {t("sale.refunded")}
                  </span>
                  <span className="font-mono text-base tabular-nums text-amber-600 dark:text-amber-400">
                    {formatCents(refundedCents, pageOrder.currency)}
                  </span>
                </span>
              )}
              {COLLECTABLE_STATUSES.has(pageOrder.status) && remainingCents > 0 && (
                <span className="flex flex-col gap-0.5">
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                    {t("sale.remaining")}
                  </span>
                  <span
                    className={cn(
                      "font-mono text-base font-bold tabular-nums",
                      remainingColorClass,
                    )}
                  >
                    {formatCents(remainingCents, pageOrder.currency)}
                  </span>
                </span>
              )}
            </div>
          </div>
          {metaExpanded ? (
            <ChevronUp className="mt-1 h-4 w-4 shrink-0 text-muted" />
          ) : (
            <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted" />
          )}
        </button>
        {metaExpanded && (
          <div className="border-t border-line px-4 pb-4 pt-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <DetailCell label={t("transactions.colReference")}>
                <span className="font-mono text-sm">
                  {pageOrder.external_reference ??
                    `#${pageOrder.id.split("-")[0]?.toUpperCase() ?? ""}`}
                </span>
              </DetailCell>
              <DetailCell label={t("transactions.colDate")}>
                <span className="tabular-nums">{formatDateTime(pageOrder.created_at, tz)}</span>
              </DetailCell>
              <DetailCell label={t("transactions.colCustomer")}>
                <div className="flex flex-col">
                  <span>{pageOrder.customer_name ?? <span className="text-faint">-</span>}</span>
                  {pageOrder.customer_phone && (
                    <span className="font-mono text-xs text-muted">{pageOrder.customer_phone}</span>
                  )}
                  {pageOrder.customer_email && (
                    <span className="text-xs text-muted">{pageOrder.customer_email}</span>
                  )}
                </div>
              </DetailCell>
              <DetailCell label={t("transactions.colDepartment")}>
                {deptName ?? <span className="text-faint">-</span>}
              </DetailCell>
            </div>
            {pageOrder.description && (
              <div className="mt-3">
                <p
                  className={cn(
                    "text-sm text-muted",
                    !descriptionExpanded && "line-clamp-1",
                    descriptionExpanded && "whitespace-pre-line",
                  )}
                >
                  {pageOrder.description}
                </p>
                {(pageOrder.description.includes("\n") || pageOrder.description.length > 60) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDescriptionExpanded((x) => !x);
                    }}
                    className="mt-0.5 text-xs font-medium text-brand-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                  >
                    {descriptionExpanded ? t("common.showLess") : t("common.showMore")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Live sections - all inlined (never inner function components) */}
      {env && (
        <div className="flex flex-col gap-4">
          {paymentError && (
            <div
              ref={errorRef}
              className={cn(
                "transition-opacity",
                isAnyFormActive && "pointer-events-none opacity-40",
              )}
            >
              <Alert tone="error">{paymentError}</Alert>
            </div>
          )}
          {hasInFlightCharge && (
            <div
              className={cn(
                "transition-opacity",
                isAnyFormActive && "pointer-events-none opacity-40",
              )}
            >
              <Alert
                tone={inFlightTimedOut ? "warn" : "info"}
                title={t(
                  inFlightTimedOut
                    ? "sale.pendingPaymentTimedOutTitle"
                    : "sale.pendingPaymentNoticeTitle",
                  { count: inFlightCount },
                )}
              >
                {inFlightTimedOut
                  ? t("sale.pendingPaymentTimedOutBody")
                  : netRemainingCents > 0
                    ? t("sale.pendingPaymentNoticeBody", { count: inFlightCount })
                    : t("sale.pendingPaymentNoticeBodyFull", { count: inFlightCount })}
              </Alert>
            </div>
          )}
          <div ref={refundRef}>
            <RefundPanel
              envelope={env}
              profile={profile.data}
              locationId={env.order.location_id}
              locked={isPaymentFormActive}
              open={refundOpen}
              onOpenChange={setRefundOpen}
              onFormActiveChange={setIsRefundFormActive}
            />
          </div>
          {collectable && (
            <div
              className={cn(
                "rounded-xl border border-line bg-surface transition-opacity",
                isRefundFormActive && "pointer-events-none opacity-40",
              )}
            >
              <div className="flex items-center justify-between rounded-t-xl border-b border-brand-primary/20 bg-brand-primary/5 px-4 py-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary">
                  {t(collectTitleKey)}
                </span>
                <span
                  className={cn(
                    "font-mono text-sm font-semibold tabular-nums",
                    remainingColorClass,
                  )}
                >
                  {formatCents(netRemainingCents, env.order.currency)}
                </span>
              </div>
              <div className="p-4">
                <MethodPicker
                  remainingCents={netRemainingCents}
                  currency={env.order.currency}
                  submitting={submitting}
                  locationId={env.order.location_id}
                  showHeading={false}
                  lastPaymentOutcome={lastPaymentOutcome}
                  onFormActiveChange={(active) => {
                    setIsPaymentFormActive(active);
                    if (!active) setLastPaymentOutcome(null);
                  }}
                  initialCustomerPhone={
                    env.order.customer?.phone ?? env.order.customer_phone ?? null
                  }
                  initialCustomerName={env.order.customer_name ?? null}
                  onSubmit={handlePaymentSubmit}
                />
              </div>
            </div>
          )}
          <div
            className={cn(
              "transition-opacity",
              isAnyFormActive && "pointer-events-none opacity-40",
            )}
          >
            <Timeline
              payments={env.payments}
              currency={env.order.currency}
              locationId={env.order.location_id}
              orderId={env.order.id}
              onCancelPayment={(paymentId) => setCancellingPaymentId(paymentId)}
              onPrintReceipt={() => setReceiptsOpen(true)}
              customerPhone={env.order.customer?.phone ?? env.order.customer_phone ?? null}
            />
          </div>
          {/* Discount management — only while order is open and no approved payment exists */}
          {env.order.status === "open" &&
            !env.payments.some((p) => p.role === "charge" && p.status === "APPROVED") &&
            discountTypes.length > 0 && (
              <DiscountManagementCard
                order={env.order}
                discountTypes={discountTypes}
                addDiscountOpen={addDiscountOpen}
                addDiscountTypeId={addDiscountTypeId}
                addDiscountMode={addDiscountMode}
                addDiscountInput={addDiscountInput}
                addDiscountReason={addDiscountReason}
                addDiscountSubmitting={addDiscountSubmitting}
                addDiscountError={addDiscountError}
                voidingDiscountId={voidingDiscountId}
                voidDiscountSubmitting={voidDiscountSubmitting}
                voidDiscountError={voidDiscountError}
                onOpenAdd={() => {
                  resetAddDiscount();
                  setAddDiscountOpen(true);
                }}
                onCloseAdd={() => {
                  setAddDiscountOpen(false);
                  resetAddDiscount();
                }}
                onAddTypeChange={setAddDiscountTypeId}
                onAddModeChange={setAddDiscountMode}
                onAddInputChange={setAddDiscountInput}
                onAddReasonChange={setAddDiscountReason}
                onAddSubmit={handleAddDiscountSubmit}
                onVoidRequest={(id) => {
                  setVoidDiscountError(null);
                  setVoidingDiscountId(id);
                }}
                onVoidConfirm={handleVoidDiscount}
                onVoidCancel={() => {
                  setVoidingDiscountId(null);
                  setVoidDiscountError(null);
                }}
              />
            )}
        </div>
      )}

      {/* Modals */}
      {receiptsOpen && env && (
        <OrderReceiptsModal
          envelope={env}
          brand={profile.data?.brand}
          onClose={() => setReceiptsOpen(false)}
        />
      )}
      {editing && (
        <EditOrderDialog
          order={pageOrder}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      {cancellingPaymentId && (
        <CancelPaymentDialog
          orderId={orderId}
          paymentId={cancellingPaymentId}
          onClose={() => setCancellingPaymentId(null)}
          onCancelled={() => {
            void envelope.refetch();
            setCancellingPaymentId(null);
          }}
        />
      )}
      {kioskHandoffOpen && (
        <KioskHandoffModal orderId={orderId} onClose={() => setKioskHandoffOpen(false)} />
      )}
    </div>
  );
}

function DiscountManagementCard({
  order,
  discountTypes,
  addDiscountOpen,
  addDiscountTypeId,
  addDiscountMode,
  addDiscountInput,
  addDiscountReason,
  addDiscountSubmitting,
  addDiscountError,
  voidingDiscountId,
  voidDiscountSubmitting,
  voidDiscountError,
  onOpenAdd,
  onCloseAdd,
  onAddTypeChange,
  onAddModeChange,
  onAddInputChange,
  onAddReasonChange,
  onAddSubmit,
  onVoidRequest,
  onVoidConfirm,
  onVoidCancel,
}: {
  readonly order: Order;
  readonly discountTypes: readonly DiscountType[];
  readonly addDiscountOpen: boolean;
  readonly addDiscountTypeId: string;
  readonly addDiscountMode: "amount" | "percent";
  readonly addDiscountInput: string;
  readonly addDiscountReason: string;
  readonly addDiscountSubmitting: boolean;
  readonly addDiscountError: string | null;
  readonly voidingDiscountId: string | null;
  readonly voidDiscountSubmitting: boolean;
  readonly voidDiscountError: string | null;
  readonly onOpenAdd: () => void;
  readonly onCloseAdd: () => void;
  readonly onAddTypeChange: (v: string) => void;
  readonly onAddModeChange: (v: "amount" | "percent") => void;
  readonly onAddInputChange: (v: string) => void;
  readonly onAddReasonChange: (v: string) => void;
  readonly onAddSubmit: () => void;
  readonly onVoidRequest: (id: string) => void;
  readonly onVoidConfirm: (id: string) => void;
  readonly onVoidCancel: () => void;
}) {
  const { t } = useTranslation();
  const activeDiscounts = order.discounts ?? [];
  const selectedDt = discountTypes.find((x) => x.id === addDiscountTypeId) ?? null;
  const canAddMore = order.discount_cents < order.total_cents;

  return (
    <div className="rounded-xl border border-line bg-surface">
      <div className="flex items-center justify-between rounded-t-xl border-b border-line px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted">
          {t("sale.discount")}
        </span>
        {canAddMore && !addDiscountOpen && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={onOpenAdd}
          >
            {t("sale.addDiscount")}
          </Button>
        )}
      </div>

      {/* Existing discount rows */}
      {activeDiscounts.length > 0 && (
        <ul className="divide-y divide-line px-4">
          {activeDiscounts.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 py-2.5">
              <span className="text-sm text-foreground">
                {d.percent_bps != null
                  ? t("receipt.discountWithPercent", {
                      label: d.discount_type_display_name,
                      percent: (d.percent_bps / 100).toFixed(2).replace(/\.?0+$/, ""),
                    })
                  : d.discount_type_display_name}
              </span>
              <span className="flex items-center gap-2">
                <span className="font-mono text-sm tabular-nums text-emerald-600 dark:text-emerald-400">
                  -{formatCents(d.amount_cents, order.currency)}
                </span>
                <button
                  type="button"
                  onClick={() => onVoidRequest(d.id)}
                  className="min-h-tap rounded-md px-1 text-muted hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                  aria-label={t("orderDetail.voidDiscount")}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Add discount form */}
      {addDiscountOpen && (
        <div className="border-t border-line p-4">
          <div className="flex flex-col gap-3">
            {addDiscountError && <Alert tone="error">{addDiscountError}</Alert>}
            <select
              value={addDiscountTypeId}
              onChange={(e) => onAddTypeChange(e.target.value)}
              className="min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={t("sale.discountType")}
            >
              <option value="">{t("sale.discountType")}</option>
              {discountTypes.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.display_name}
                </option>
              ))}
            </select>
            {selectedDt?.is_other && (
              <input
                type="text"
                value={addDiscountReason}
                onChange={(e) => onAddReasonChange(e.target.value)}
                placeholder={t("sale.discountReason")}
                maxLength={200}
                className="min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm text-foreground placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary"
              />
            )}
            {addDiscountTypeId && (
              <div className="flex items-center gap-2">
                <div className="flex overflow-hidden rounded-md border border-line">
                  <button
                    type="button"
                    onClick={() => onAddModeChange("amount")}
                    className={cn(
                      "min-h-tap px-3 text-sm font-medium transition",
                      addDiscountMode === "amount"
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface text-muted hover:text-foreground",
                    )}
                  >
                    {t("sale.discountModeAmount")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onAddModeChange("percent")}
                    className={cn(
                      "min-h-tap px-3 text-sm font-medium transition",
                      addDiscountMode === "percent"
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface text-muted hover:text-foreground",
                    )}
                  >
                    {t("sale.discountModePercent")}
                  </button>
                </div>
                <input
                  type="text"
                  value={addDiscountInput}
                  onChange={(e) => onAddInputChange(e.target.value)}
                  placeholder={addDiscountMode === "amount" ? "0.00" : "0"}
                  inputMode="decimal"
                  className="min-h-tap w-28 rounded-md border border-line bg-surface px-3 font-mono text-sm text-foreground placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary"
                  aria-label={t("sale.discount")}
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onCloseAdd}
                disabled={addDiscountSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onAddSubmit}
                disabled={addDiscountSubmitting || !addDiscountTypeId || !addDiscountInput.trim()}
              >
                {addDiscountSubmitting ? t("common.saving") : t("sale.addDiscount")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Void confirmation */}
      {voidingDiscountId && (
        <div className="border-t border-line bg-rose-50 p-4 dark:bg-rose-950/20">
          {voidDiscountError && (
            <Alert tone="error" className="mb-3">
              {voidDiscountError}
            </Alert>
          )}
          <p className="mb-3 text-sm text-foreground">{t("orderDetail.voidDiscountConfirm")}</p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onVoidCancel}
              disabled={voidDiscountSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => onVoidConfirm(voidingDiscountId)}
              disabled={voidDiscountSubmitting}
            >
              {voidDiscountSubmitting ? t("common.saving") : t("orderDetail.voidDiscount")}
            </Button>
          </div>
        </div>
      )}

      {activeDiscounts.length === 0 && !addDiscountOpen && (
        <p className="px-4 py-3 text-sm text-faint">{t("sale.noDiscounts")}</p>
      )}
    </div>
  );
}

function DetailCell({
  label,
  children,
  className,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
        {label}
      </span>
      <span className="text-sm text-strong">{children}</span>
    </div>
  );
}
